import React, { useState } from 'react';
import { Clock, Check, X, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const InfoTab = ({
  formData, setFormData, serviceDate, nameMatchesType,
  hasEditPermission, serviceTypes, myAssignment, handleRespond, respondingAction,
  onDone,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftData, setDraftData] = useState({});
  const isPast = serviceDate ? serviceDate < new Date(new Date().setHours(0, 0, 0, 0)) : false;

  const startEditing = () => {
    setDraftData({
      service_type: formData.service_type || '',
      name: formData.name || '',
      date: formData.date || '',
      time: formData.time || '',
      notes: formData.notes || '',
    });
    setIsEditing(true);
  };

  const handleDone = () => {
    setFormData(prev => ({ ...prev, ...draftData }));
    setIsEditing(false);
    onDone && onDone();
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="info-tab-content">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Edit Service Info</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="cancel-edit-info-btn" className="rounded-full gap-2" onClick={handleCancel}>
              <X className="w-3.5 h-3.5" /> Cancel
            </Button>
            <Button variant="default" size="sm" data-testid="done-edit-info-btn" className="rounded-full gap-2 bg-green-600 hover:bg-green-700 text-white" onClick={handleDone}>
              <Check className="w-3.5 h-3.5" /> Done
            </Button>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Service Type *</Label>
            <Select value={draftData.service_type || undefined} onValueChange={(val) => setDraftData(prev => ({ ...prev, service_type: val }))}>
              <SelectTrigger data-testid="edit-service-type" className="mt-1"><SelectValue placeholder="Select service type" /></SelectTrigger>
              <SelectContent className="bg-white">{serviceTypes.map(t => <SelectItem key={t.service_type_id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="name">Service Name (optional)</Label>
            <Input id="name" data-testid="edit-service-name" value={draftData.name}
              onChange={(e) => setDraftData(prev => ({ ...prev, name: e.target.value }))} placeholder="Leave blank to use service type as name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label htmlFor="date">Date *</Label><Input id="date" type="date" data-testid="edit-service-date" value={draftData.date} onChange={(e) => setDraftData(prev => ({ ...prev, date: e.target.value }))} required /></div>
            <div><Label htmlFor="time">Time *</Label><Input id="time" type="time" data-testid="edit-service-time" value={draftData.time} onChange={(e) => setDraftData(prev => ({ ...prev, time: e.target.value }))} required /></div>
          </div>
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" data-testid="edit-service-notes" value={draftData.notes}
              onChange={(e) => setDraftData(prev => ({ ...prev, notes: e.target.value }))} rows={3} />
          </div>
        </div>
      </div>
    );
  }

  const allAssignments = (formData.team_assignments || [])
    .flatMap(g => g.assignments || [])
    .filter(a => a.member_id);
  const accepted = allAssignments.filter(a => a.status?.toLowerCase() === 'accepted').length;
  const declined = allAssignments.filter(a => a.status?.toLowerCase() === 'declined').length;
  const pending = allAssignments.filter(a => !a.status || a.status.toLowerCase() === 'pending').length;
  const total = allAssignments.length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="info-tab-content">
      <div className="flex items-start justify-between mb-6">
        <div>
          {serviceDate && (
            <>
              <h1 className="text-xl sm:text-3xl font-bold text-slate-900" data-testid="service-date-header">{format(serviceDate, 'dd MMM')}</h1>
              <p className="text-sm sm:text-base text-slate-700 mt-1" data-testid="service-type-name">
                {formData.service_type}{formData.name && !nameMatchesType ? ` — ${formData.name}` : ''}
              </p>
            </>
          )}
        </div>
        {hasEditPermission && (
          <Button variant="outline" size="sm" data-testid="edit-info-btn" className="rounded-full gap-2 flex-shrink-0" onClick={startEditing}>
            <Pencil className="w-3.5 h-3.5" /> Edit
          </Button>
        )}
      </div>
      <div className="space-y-3 text-sm">
        {serviceDate && (
          <div className="flex items-center gap-3 text-slate-600" data-testid="service-datetime-display">
            <Clock className="w-4 h-4 text-slate-400 shrink-0" />
            <span>{format(serviceDate, 'EEE, dd MMM yyyy')} · {format(serviceDate, 'h:mm a').replace('am','AM').replace('pm','PM')}</span>
          </div>
        )}
        {formData.notes && (
          <div className="pt-3 mt-3 border-t border-slate-100">
            <p className="text-sm text-slate-600 whitespace-pre-wrap" data-testid="service-notes-display">{formData.notes}</p>
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="mt-5 pt-5 border-t border-slate-100" data-testid="info-assignment-stats">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Team Responses</p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-5 text-sm">
            <span className="flex items-center gap-1.5 text-emerald-600 font-medium"><Check className="w-4 h-4" />{accepted} Accepted</span>
            <span className="flex items-center gap-1.5 text-rose-500 font-medium"><X className="w-4 h-4" />{declined} Declined</span>
            <span className="flex items-center gap-1.5 text-amber-500 font-medium"><Clock className="w-4 h-4" />{pending} Pending</span>
          </div>
        </div>
      )}

      {myAssignment && (
        <div className="mt-5 pt-5 border-t border-slate-100" data-testid="info-my-assignment">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">My Assignment</p>
          <p className="text-sm text-green-600 font-medium mb-3">{myAssignment.roles.join(', ')}</p>
          {isPast ? (
            <p className="text-xs text-slate-400 italic" data-testid="past-service-notice">This service has already passed.</p>
          ) : (
          <div className="inline-flex rounded-lg overflow-hidden border border-slate-200" data-testid="response-toggle">
            <button onClick={() => handleRespond(myAssignment.status === 'accepted' ? 'pending' : 'accepted', 'accept')} data-testid="info-accept-btn"
              disabled={!!respondingAction}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                myAssignment.status === 'accepted'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-slate-400 hover:bg-emerald-50 hover:text-emerald-600'
              }`}>
              {respondingAction === 'accept'
                ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <Check className="w-3.5 h-3.5" />
              }
              {myAssignment.status === 'accepted' ? 'Accepted' : 'Accept'}
            </button>
            <button onClick={() => handleRespond(myAssignment.status === 'declined' ? 'pending' : 'declined', 'decline')} data-testid="info-decline-btn"
              disabled={!!respondingAction}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors border-l border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed ${
                myAssignment.status === 'declined'
                  ? 'bg-rose-600 text-white'
                  : 'bg-white text-slate-400 hover:bg-rose-50 hover:text-rose-600'
              }`}>
              {respondingAction === 'decline'
                ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <X className="w-3.5 h-3.5" />
              }
              {myAssignment.status === 'declined' ? 'Declined' : 'Decline'}
            </button>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InfoTab;
